import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  AuthUser,
  ActiveStore,
  MultiStoreTemp,
  AuthState,
} from "../types/auth";
import { registerAuthStore } from "../api/auth-api";

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      tokenExpiry: null,
      deviceId: "",
      user: null,
      store: null,
      shopId: "",
      shopName: "",
      multiStoreTemp: null,

      setDeviceId: (deviceId: string) => {
        set({ deviceId });
      },

      setSession: (token: string, user: AuthUser, store: ActiveStore) =>
        set({
          token,
          tokenExpiry: Date.now() + 24 * 60 * 60 * 1000,
          user,
          store,
          multiStoreTemp: null,
          shopId: store.shopId,
          // Persist the real store name from the server so the login
          // screen can greet the user by store on next launch / after
          // logout. Previously this echoed shopId, losing the proper
          // display name.
          shopName: store.shopName,
        }),

      setStoreConfig: (shopId: string, shopName: string) =>
        set({ shopId, shopName }),

      setMultiStoreTemp: (temp: MultiStoreTemp) =>
        set({ multiStoreTemp: temp }),

      clearMultiStoreTemp: () => set({ multiStoreTemp: null }),

      clearSession: () =>
        set({
          token: null,
          tokenExpiry: null,
          user: null,
          store: null,
          multiStoreTemp: null,
        }),

      isTokenExpired: () => {
        const { tokenExpiry } = get();
        if (tokenExpiry == null) return true;
        return Date.now() > tokenExpiry;
      },
    }),
    {
      name: "pos-auth-store",
      partialize: (state) => ({
        token: state.token,
        tokenExpiry: state.tokenExpiry,
        deviceId: state.deviceId,
        user: state.user,
        store: state.store,
        shopId: state.shopId,
        shopName: state.shopName,
      }),
    },
  ),
);

// Register the store with auth-api so getAuthHeaders()
// and handleAuthError() can read state without circular imports.
registerAuthStore(useAuthStore);
